'use client';

/* Onglets de filtre par catégorie, pilotés par props (réutilisé booking + carte cadeau).
   La rangée scrolle horizontalement (scrollbar masquée) ; des flèches prev/next apparaissent
   quand le contenu déborde, chacune masquée en butée de son côté. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DEFAULT_LABELS } from '../../types';

export interface CategoryTab {
  catalog_category_id: number;
  name: string;
}

interface CategoryGroupProps {
  categories: CategoryTab[];
  activeCategoryId: number | null;
  onChange: (id: number | null) => void;
  allCategoriesLabel: string;
  scrollPrevLabel?: string;
  scrollNextLabel?: string;
}

export function CategoryGroup({
  categories,
  activeCategoryId,
  onChange,
  allCategoriesLabel,
  scrollPrevLabel = DEFAULT_LABELS.categories_scroll_prev,
  scrollNextLabel = DEFAULT_LABELS.categories_scroll_next,
}: CategoryGroupProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateArrows = useCallback((): void => {
    const el = scrollerRef.current;
    if (!el) return;
    /* Seuil de 1px pour absorber les positions fractionnaires de scroll. */
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollPrev(el.scrollLeft > 1);
    setCanScrollNext(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect((): (() => void) | undefined => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    return (): void => {
      el.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [updateArrows, categories.length]);

  const scrollByStep = (direction: -1 | 1): void => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.75, 160), behavior: 'smooth' });
  };

  if (categories.length <= 1) return null;

  return (
    <div className="bt-categories-nav">
      {canScrollPrev && (
        <button
          type="button"
          className="bt-categories-nav__arrow bt-categories-nav__arrow--prev"
          aria-label={scrollPrevLabel}
          onClick={(): void => scrollByStep(-1)}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
      <div className="bt-categories" ref={scrollerRef}>
        <button
          type="button"
          className={`bt-category-tab ${activeCategoryId === null ? 'bt-category-tab--active' : ''}`}
          onClick={(): void => onChange(null)}
        >
          {allCategoriesLabel}
        </button>
        {categories.map((cat) => (
          <button
            key={cat.catalog_category_id}
            type="button"
            className={`bt-category-tab ${activeCategoryId === cat.catalog_category_id ? 'bt-category-tab--active' : ''}`}
            onClick={(): void => onChange(cat.catalog_category_id)}
          >
            {cat.name}
          </button>
        ))}
      </div>
      {canScrollNext && (
        <button
          type="button"
          className="bt-categories-nav__arrow bt-categories-nav__arrow--next"
          aria-label={scrollNextLabel}
          onClick={(): void => scrollByStep(1)}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
